import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { useUpdateInvoiceForwardingSettings, useUser } from '~/api/user';
import { Button, Dialog, ExternalLink, Infotip } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import { formStyle, stepsStyle, titleStyle } from './ExternalSystemModals.css';

type Props = {
  onClose: () => void;
};

export const DexxterModal = ({ onClose }: Props) => {
  const { data: user } = useUser();
  const mutation = useUpdateInvoiceForwardingSettings();

  const { control, handleSubmit } = useForm<{ emailAddress: string }>({
    defaultValues: {
      emailAddress: '',
    },
  });

  const { t } = useTranslation(['settings', 'validation']);

  const onSubmit = handleSubmit((data) => {
    mutation.mutate(
      {
        entries: [
          ...user.invoiceForwardingSettings,
          {
            frequency: 'instant',
            receiverEmail: `${data.emailAddress.trim()}@inbound.dexxter.be`,
            platformEmail: null,
          },
        ],
      },
      {
        onSuccess: () => {
          toast.success({ description: t('settings:integrations.externalSystems.systems.dexxter.alerts.success') });
          onClose();
        },
        onError: () => toast.danger({ description: t('integrations.externalSystems.systems.dexxter.alerts.error') }),
      },
    );
  });

  return (
    <Dialog.Root onOpenChange={(open) => !open && onClose()} open>
      <Dialog.Body>
        <Dialog.Header
          title={
            <span className={titleStyle}>
              {t('settings:integrations.externalSystems.systems.dexxter.modal.title')}

              <Infotip>
                <Trans
                  components={{ faqLink: <ExternalLink /> }}
                  i18nKey="settings:integrations.externalSystems.systems.dexxter.modal.titleTooltip"
                />
              </Infotip>
            </span>
          }
        />

        <p>
          <Trans
            components={{
              dexxterLink: <ExternalLink />,
              ol: <ol className={stepsStyle} />,
              li: <li />,
            }}
            i18nKey="settings:integrations.externalSystems.systems.dexxter.modal.description"
          />
        </p>

        <Form className={formStyle} onSubmit={onSubmit}>
          <Controller
            control={control}
            name="emailAddress"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
              <Field.Root invalid={invalid} name={name}>
                <Input
                  autoFocus
                  onBlur={onBlur}
                  onValueChange={onChange}
                  placeholder={t('settings:integrations.externalSystems.systems.dexxter.modal.emailAddress.placeholder')}
                  ref={ref}
                  suffix={t('settings:integrations.externalSystems.systems.dexxter.modal.emailAddress.suffix')}
                  transformValue={(value) => (value.trim().endsWith('@inbound.dexxter.be') ? value.trim().split('@')[0] : value)}
                  value={value}
                />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              validate: {
                notBlank: (value) => isNotBlank(value) || t('validation:required'),
                prefix: (value) =>
                  value.trim().startsWith('income.') ||
                  t('settings:integrations.externalSystems.systems.dexxter.modal.emailAddress.validatePrefix'),
                suffix: (value) =>
                  !value.includes('@') || t('settings:integrations.externalSystems.systems.dexxter.modal.emailAddress.validateSuffix'),
              },
            }}
          />

          <Dialog.Actions>
            <Button icon="Link" loading={mutation.isPending} type="submit">
              {t('settings:integrations.pair')}
            </Button>
          </Dialog.Actions>
        </Form>
      </Dialog.Body>
    </Dialog.Root>
  );
};
